import React, { useEffect, useState } from "react";
import "./styles.css";
import { Container, Table } from "react-bootstrap";
import LoadingPage from "../../Components/LoadingPage/LoadingPage";
import { DeviceManagementData } from "../../types/dataType";
import { getDeviceManagementAPI } from "../../apiRequests/device-management/getDeviceManagementAPI";
import { computeDeviceManagementKPI, convertToPercentage } from "../../utils/commonUtils";
import { NA_CALIBRATION_DATE, OUT_OF_SERVICE_STATUS } from "../../constants/appConstants";

interface State {
    inCalibrationDevices: DeviceManagementData[];
    outOfCalibrationDevices: DeviceManagementData[];
    outOfServiceDevices: DeviceManagementData[];
    kpi: number;
    totalDevices: number;
}
interface InternalAuditProps {
    isDashboard?: boolean;
    deviceManagementData: DeviceManagementData[];
}

const DeviceManagement = (props: InternalAuditProps) => {
    const [deviceManagementState, setDeviceManagementState] = useState<State>({
        inCalibrationDevices: [],
        outOfCalibrationDevices: [],
        outOfServiceDevices: [],
        kpi: 0,
        totalDevices: 0,
    });
    const [isloading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!props.isDashboard) retrieveDeviceManagementDate();
        else computeKPIData(props.deviceManagementData);
    }, []);

    const retrieveDeviceManagementDate = async () => {
        setLoading(true);
        const data = await getDeviceManagementAPI();
        computeKPIData(data);
        setLoading(false);
    };

    const computeKPIData = (deviceManagementData: DeviceManagementData[]) => {
        const workingDevices = deviceManagementData.filter(
            (device) => device.deviceStatus.toLocaleLowerCase() !== OUT_OF_SERVICE_STATUS.toLocaleLowerCase()
        );
        const outOfServiceDevices = deviceManagementData.filter(
            (data: DeviceManagementData) => data.deviceStatus.toLocaleLowerCase() === OUT_OF_SERVICE_STATUS.toLocaleLowerCase()
        );
        const inCalibrationDevices = workingDevices.filter((device) => {
            if (NA_CALIBRATION_DATE.includes(device.nextCalibrationDate.trim().toUpperCase())) return true;
            const today = new Date();
            const calibrationDate = new Date(device.nextCalibrationDate);
            return today <= calibrationDate;
        });
        const outOfCalibrationDevices = workingDevices.filter((device) => !inCalibrationDevices.includes(device));
        const KPI = computeDeviceManagementKPI(workingDevices);

        setDeviceManagementState({
            inCalibrationDevices: inCalibrationDevices,
            outOfCalibrationDevices: outOfCalibrationDevices,
            outOfServiceDevices: outOfServiceDevices,
            kpi: KPI,
            totalDevices: deviceManagementData.length,
        });
    };

    const renderDevicesTable = (tableData: DeviceManagementData[], title: string) => {
        return (
            Boolean(tableData.length) && (
                <Container>
                    <p>
                        <b>{title}</b>
                    </p>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th></th>
                                <th>Equipment ID</th>
                                <th>Equipment</th>
                                <th>Calibration Date</th>
                                <th>Status</th>
                                <th>Next Calibration Due Date</th>
                                <th>Certificates Reviewed by:</th>
                                <th>Equipment File's location</th>
                                <th>Remark</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((data: DeviceManagementData, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{data.deviceId}</td>
                                    <td>{data.deviceName}</td>
                                    <td>{data.calibrationDate}</td>
                                    <td>{data.deviceStatus}</td>
                                    <td>{data.nextCalibrationDate}</td>
                                    <td>{data.certificatesReviewedBy}</td>
                                    <td>
                                        <a target="_blank" href={data.equipmentFileLocation.link}>
                                            {data.equipmentFileLocation.text}
                                        </a>
                                    </td>
                                    <td>{data.remark}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const renderOutOfServiceDevices = () => {
        return renderDevicesTable(deviceManagementState.outOfServiceDevices, "Out Of Service Devices");
    };

    const renderInCalibrationDevices = () => {
        return renderDevicesTable(deviceManagementState.inCalibrationDevices, "In Calibration Devices");
    };

    const renderOutOfCalibrationDevices = () => {
        return renderDevicesTable(deviceManagementState.outOfCalibrationDevices, "Out Of Calibration Devices");
    };

    const renderTableKPI = () => {
        const { kpi, totalDevices, inCalibrationDevices, outOfCalibrationDevices, outOfServiceDevices } = deviceManagementState;
        return (
            <Container style={{ marginTop: "8rem" }}>
                <h2>Device Management KPI</h2>
                <Table>
                    <thead>
                        <tr>
                            <th>KPI</th>
                            <th>In Calibration</th>
                            <th>Out Of Calibration</th>
                            <th>Out Of Service</th>
                            <th>TOTAL</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{convertToPercentage(kpi)}</td>
                            <td>{inCalibrationDevices.length}</td>
                            <td>{outOfCalibrationDevices.length}</td>
                            <td>{outOfServiceDevices.length}</td>
                            <td>{totalDevices}</td>
                        </tr>
                    </tbody>
                </Table>
            </Container>
        );
    };

    const renderLoading = () => {
        return <LoadingPage></LoadingPage>;
    };

    const renderMainContentOfDeviceManagement = () => {
        return (
            <>
                {renderTableKPI()}
                {renderInCalibrationDevices()}
                {renderOutOfCalibrationDevices()}
                {renderOutOfServiceDevices()}
            </>
        );
    };
    return <Container>{isloading ? renderLoading() : renderMainContentOfDeviceManagement()}</Container>;
};

export default DeviceManagement;
